import React from 'react';
import PropTypes from 'prop-types';
import { IcoSun, IcoMoon, IcoMonitor } from './icons';
import { PREFERENCES, useTheme } from '../../context/ThemeContext';

// Three-way appearance control (STRY0039116): System · Light · Dark.
// "System" follows the OS/browser setting and is the default for anyone who
// has never chosen; the top-bar quick toggle writes an explicit Light/Dark,
// and this is where a user gets back to following the OS.
const LABELS = {
  system: { label: 'System', hint: 'Follow the operating system setting', Icon: IcoMonitor },
  light: { label: 'Light', hint: 'Always light', Icon: IcoSun },
  dark: { label: 'Dark', hint: 'Always dark', Icon: IcoMoon }
};

const ThemePicker = ({ compact }) => {
  const { preference, theme, setPreference } = useTheme();
  return (
    <div className="bz-stack" style={{ gap: 8 }}>
      <div
        role="radiogroup"
        aria-label="Appearance"
        className="bz-row"
        style={{
          gap: 0,
          display: 'inline-flex',
          border: '1px solid var(--line)',
          borderRadius: 'var(--r-md)',
          background: 'var(--bg-soft)',
          padding: 3,
          width: 'fit-content'
        }}
      >
        {PREFERENCES.map(key => {
          const { label, hint, Icon } = LABELS[key];
          const active = preference === key;
          return (
            <button
              key={key}
              type="button"
              role="radio"
              aria-checked={active}
              title={hint}
              data-testid={`theme-${key}`}
              onClick={() => setPreference(key)}
              style={{
                all: 'unset',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: compact ? '4px 10px' : '6px 14px',
                borderRadius: 'var(--r-sm)',
                fontSize: 'var(--fs-md)',
                fontWeight: active ? 600 : 500,
                color: active ? 'var(--ink)' : 'var(--ink-soft)',
                background: active ? 'var(--bg-elev)' : 'transparent',
                boxShadow: active ? 'var(--shadow-1)' : 'none',
                transition: 'background .12s, color .12s'
              }}
            >
              <Icon size={14} />
              {label}
            </button>
          );
        })}
      </div>
      {!compact && (
        <div className="bz-muted" style={{ fontSize: 'var(--fs-sm)' }} data-testid="theme-status">
          {preference === 'system'
            ? `Following your system setting (currently ${theme}).`
            : `Always ${theme}. Choose System to follow your operating system again.`}
        </div>
      )}
    </div>
  );
};

ThemePicker.propTypes = { compact: PropTypes.bool };

export default ThemePicker;
